import { useState, useEffect, useCallback, Fragment, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import * as _ from 'lodash';
import { Col, Row } from 'react-bootstrap';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import MatchCard from 'components/general/match_card.js';
import { getSportTypeToActiveIcon, getSportTypeToWhiteIcon } from 'toolkit/utils';
import SportDetailItem from 'components/general/SportDetailItem';
import { fetchLiveMatches, setNextLiveMatches, setSelectedSport } from 'stores/match';
import eventsToGroup from 'toolkit/eventsToGroup.js';
import GoBack from 'components/general/go_back';
import { AWS_BUCKET_URL } from 'toolkit/constants';
import { useAllowedSports } from 'toolkit/useHooks/useAllowedSports';

function SportDetail() {
  const matchCardListRef = useRef(null);
  const dispatch = useDispatch();
  const selectedSport = useSelector((state) => state.match.selectedSport);
  const selectedMatch = useSelector((state) => state.match.selectedMatch);
  const selectedLocale = useSelector((state) => state.match.selectedLocale);
  const locales = useSelector((state) => state.match.locales);
  const events = useSelector((state) => state.match.events);
  const liveEvents = useSelector((state) => {
    const { liveMatches, events } = state.match;
    return liveMatches?.map((c) => events[c]);
  }, shallowEqual);
  const { t } = useTranslation('translations', { useSuspense: false });
  const [lastScrollPosition, setLastScrollPosition] = useState(
    localStorage.getItem('scrollPosition') || 0
  );
  const [sports, setSports] = useState({});
  const [sport, setSport] = useState('');
  const [selectedMarketType, setSelectedMarketType] = useState(7);
  const location = useLocation();
  const { isSportAllowed } = useAllowedSports();

  const handleLocationScroll = () => {
    const currentPosition = window.pageYOffset || document.documentElement.scrollTop;
    setLastScrollPosition(currentPosition);
  };

  useEffect(() => {
    window.addEventListener('scroll', handleLocationScroll);

    return () => {
      window.removeEventListener('scroll', handleLocationScroll);
    };
  }, []);

  useEffect(() => {
    localStorage.setItem('scrollPosition', lastScrollPosition.toString());
  }, [lastScrollPosition]);

  useEffect(() => {
    const savedScrollPosition = localStorage.getItem('lastScrollPosition');
    if (savedScrollPosition && document.querySelector('.scrollable-y')) {
      setTimeout(() => {
        document.querySelector('.scrollable-y').scrollTop = Number(savedScrollPosition);
        // Clear the saved position after restoring
        localStorage.removeItem('lastScrollPosition');
      }, 100);
    }
  }, []);

  const fetchData = async () => {
    try {
      dispatch(fetchLiveMatches());
    } catch (error) {
      console.log("Can't fetch live matches", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  
  // useEffect(() => {
  //   useEffect(() => {
  //     const handleVisibilityChange = () => {
  //       if (!document.hidden) {
  //         fetchData();
  //       }
  //     };
  //     document.addEventListener('visibilitychange', handleVisibilityChange);
  //     fetchData();
  //     return () => {
  //       document.removeEventListener('visibilitychange', handleVisibilityChange);
  //     };
  //   }, []);
  // }, []);

  useEffect(() => {
    if (selectedMatch) {
      const selectedMatchCard = document.querySelector(`a[href*="${selectedMatch}"]`);
      if (selectedMatchCard) {
        selectedMatchCard.scrollIntoView({ block: 'center' });
      }
    }
  }, []);

  useEffect(() => {
    // if (loading) return;
    const groups = eventsToGroup(liveEvents);
    setSports(groups);
    if (sport === '' && liveEvents.length > 0) {
      const type =
        selectedSport !== null && groups[selectedSport] || sports.hasOwnProperty(Number.parseInt(selectedSport)) === true
          ? selectedSport
          : Object.keys(groups)[0];
      setSport(type);
    }
  }, [sport, liveEvents]);

  useEffect(() => {
    if (sport !== '') {
      dispatch(setSelectedSport(sport));
    }
  }, [sport]);

  const selectSport = useCallback((id) => {
    setSport(id);
    dispatch(setNextLiveMatches());
  });

  const lastScrollRef = useRef(lastScrollPosition);

  useEffect(() => {
    lastScrollRef.current = lastScrollPosition;
  }, [lastScrollPosition]);

  const handleScroll = useCallback(() => {
    const container = window;
    const stickyEl = document.querySelector('.tip-types');
    if (!stickyEl) return;
    const scrollTop = container.scrollY;
    const direction = scrollTop < lastScrollRef.current ? 'up' : 'down';
    setLastScrollPosition(scrollTop);

    if (scrollTop > 110) {
      stickyEl.classList.add('fixed');
      if (direction === 'down') {
        if (scrollTop > 200) {
          stickyEl.classList.remove('fixed');
        }
      } else {
        if (scrollTop > 200) {
          stickyEl.classList.add('fixed');
        }
      }
    } else {
      stickyEl.classList.remove('fixed');
    }
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  function calculateMenuItemsDynamic() {
    const menuItems = [];
    let icon = '';
    Object.keys(sports).map((sportType, index) => {
      if (sportType === sport) {
        icon = getSportTypeToActiveIcon(sport);
      } else {
        icon = getSportTypeToWhiteIcon(sportType);
      }
      if (isSportAllowed(sportType) && sports[sportType].length > 0) {
        menuItems.push(
          <SportDetailItem
            key={index}
            active={sport === sportType}
            sportType={sportType}
            selectedSportType={sport}
            count={_.sum(sports[sportType]?.map((l) => l.matches.length))}
            icon={icon}
            onClick={() => {
              selectSport(sportType);
            }}
            text={t('sports.' + sportType)}
            className={'d-flex flex-column padding-header-icons'}
          />
        );
      }
    });

    return menuItems;
  }

  function getBetType() {
    const betTypes = [
      {
        label: t('rom'),
        id: 7
      },
      {
        label: t('winner'),
        id: 1
      },
      {
        label: t('overunder'),
        id: 18
      },
      {
        label: t('handicap'),
        id: 14
      },
      {
        label: t('nextGoal'),
        id: 8
      },
      {
        label: t('bs'),
        id: 29
      }
    ];
    return (
      <div className="tip-types shadow">
        {betTypes?.map((betType) => (
          <div className="tip-type" key={betType.id}>
            <button
              className={`btn button btn-sm w-100 border fs-12 ${
                selectedMarketType === betType.id ? 'selected' : ''
              }`}
              onClick={() => setSelectedMarketType(betType.id)}>
              {betType.label}
            </button>
          </div>
        ))}
      </div>
    );
  }

  function getBets() {
    const matchCards = [];
    const favoriteMatchIds = new Set();
    const favoriteMatchesByLeague = {};
    
    // Collect all favorite matches grouped by league
    if (sports[sport] && sports[sport].length > 0) {
      sports[sport]
        .filter((l) => l.matches.length > 0)
        .forEach((liveMatch) => {
          liveMatch.matches.forEach((match) => {
            if (events[match.providerId]?.isFavorite) {
              favoriteMatchIds.add(match.providerId);
              const leagueKey = liveMatch.category + ' / ' + liveMatch.league;
              if (!favoriteMatchesByLeague[leagueKey]) {
                favoriteMatchesByLeague[leagueKey] = {
                  category: liveMatch.category,
                  league: liveMatch.league,
                  categoryId: match.categoryId,
                  matches: []
                };
              }
              favoriteMatchesByLeague[leagueKey].matches.push(match);
            }
          });
        });
    }
    
    // Display favorites grouped by league at the top
    Object.keys(favoriteMatchesByLeague).forEach((leagueKey) => {
      const favoriteGroup = favoriteMatchesByLeague[leagueKey];
      if (favoriteGroup.matches.length > 0) {
        let code = null;
        if (favoriteGroup.categoryId) {
          code = favoriteGroup.categoryId.split(':')[2];
        }
        
        matchCards.push(
          <Fragment key={'favorite-' + leagueKey}>
            <div className="league-names pt-0">
              {code && (
                <img
                  width="20"
                  height="13"
                  src={`${AWS_BUCKET_URL}/ClientFlags/${code}.svg`}
                  alt={code}
                  className="me-2"
                />
              )}
              {favoriteGroup.category + ' / ' + favoriteGroup.league}
            </div>
            {favoriteGroup.matches.map((match, index) => (
              <MatchCard
                key={match.providerId}
                providerId={match.providerId}
                marketType={selectedMarketType}
                index={index}
                sport={sport}
                path={location.pathname}
                locales={locales}
                selectedLocale={selectedLocale}
              />
            ))}
          </Fragment>
        );
      }
    });
    
    // Display regular live matches (excluding favorites)
    if (sports[sport] && sports[sport].length > 0) {
      sports[sport]
        .filter((l) => l.matches.length > 0)
        .forEach((liveMatch) => {
          // Filter out favorites
          const nonFavoriteMatches = liveMatch.matches.filter(
            (match) => !favoriteMatchIds.has(match.providerId)
          );
          
          if (nonFavoriteMatches.length === 0) return;

          let code = null;
          if (nonFavoriteMatches && nonFavoriteMatches.length > 0 && nonFavoriteMatches[0].categoryId) {
            code = nonFavoriteMatches[0].categoryId.split(':')[2];
          }
          matchCards.push(
            <Fragment key={nonFavoriteMatches[0]?.providerId || liveMatch.matches[0].providerId}>
              <div className="league-names pt-0">
                 {code && (
                  <img
                    width="20"
                    height="13"
                    src={`${AWS_BUCKET_URL}/ClientFlags/${code}.svg`}
                    alt={code}
                    className="me-2"
                  />
                )}
                {liveMatch.category + ' / ' + liveMatch.league}
              </div>
              {nonFavoriteMatches?.map((match, index) => (
                <MatchCard
                  key={match.providerId}
                  providerId={match.providerId}
                  marketType={selectedMarketType}
                  index={index}
                  sport={sport}
                  path={location.pathname}
                  locales={locales}
                  selectedLocale={selectedLocale}
                />
              ))}
            </Fragment>
          );
        });
    }
    
    // Check if there are no matches to display
    if (matchCards.length === 0) {
      return (
        <div className="text-center">
          <div className="gray-text mb-2 mt-5">{t('noLiveMatches')}</div>
        </div>
      );
    }
    
    return matchCards;
  }

  function getBetTypeTitle() {
    if (selectedMarketType === 29) {
      return (
        <Row>
          <Col xs={4} className="textTitle"></Col>
          <Col xs={4} className="textTitle">
            Yes
          </Col>
          <Col xs={4} className="textTitle" style={{ display: 'flex' }}>
            No
          </Col>
        </Row>
      );
    }
    if (selectedMarketType === 8) {
      return (
        <Row>
          <Col xs={4} className="textTitle">
            1
          </Col>
          <Col xs={4} className="textTitle">
            X
          </Col>
          <Col xs={4} className="textTitle">
            2
          </Col>
        </Row>
      );
    }
    if (selectedMarketType === 14) {
      return (
        <Row>
          <Col xs={4} className="textTitle">
            1
          </Col>
          <Col xs={4} className="textTitle">
            X
          </Col>
          <Col xs={4} className="textTitle">
            2
          </Col>
        </Row>
      );
    }
    if (selectedMarketType === 18) {
      return (
        <Row>
          <Col xs={4} className="textTitle"></Col>
          <Col xs={4} className="textTitle">
            +
          </Col>
          <Col xs={4} className="textTitle">
            -
          </Col>
        </Row>
      );
    }
    if (selectedMarketType === 1 || selectedMarketType === 7) {
      return (
        <Row>
          <Col xs={4} className="textTitle">
            1
          </Col>
          <Col xs={4} className="textTitle">
            X
          </Col>
          <Col xs={4} className="textTitle">
            2
          </Col>
        </Row>
      );
    }

    return (
      <Row>
        <Col xs={4} className="textTitle">
          1X
        </Col>
        <Col xs={4} className="textTitle">
          12
        </Col>
        <Col xs={4} className="textTitle">
          X2
        </Col>
      </Row>
    );
  }

  function checkSportTypes(sport) {
    if (
      sport === '1' ||
      sport === '2' ||
      sport === '4' ||
      sport === '3' ||
      sport === '6' ||
      sport === '12' ||
      sport === '13' ||
      sport === '5' ||
      sport === '11' ||
      sport === '14'
    ) {
      return true;
    }
    return false;
  }

  return (
    <div className="g-0 container-fluid scrollable-y">
      <div
        className="overflow-auto text-nowrap d-flex align-items-center px-2"
        style={{
          minHeight: '36px',
          paddingTop: '4px',
          paddingBottom: '2px',
          backgroundColor: '#000'
        }}>
        <div
          className="d-flex align-items-center justify-content-center flex-shrink-0"
          style={{
            padding: '2px 4px',
            borderRadius: '4px',
            backgroundColor: 'rgba(255, 255, 255, 0.12)',
            marginRight: '4px'
          }}>
          <div style={{ filter: 'invert(1) brightness(2)' }}>
            <GoBack />
          </div>
        </div>
        <div
          className="scrollmenuFullWidth d-flex flex-nowrap overflow-auto"
          style={{ flex: 1, minWidth: 0, marginBottom: 0 }}>
          {calculateMenuItemsDynamic()}
        </div>
      </div>
      {sport === '0' && (
        <>
          <>
            <Row>
              <Col className="tip-types-container">{getBetType()}</Col>
            </Row>
            <Row>
              <Col></Col>
              <Col>{getBetTypeTitle(selectedMarketType)}</Col>
            </Row>
          </>
        </>
      )}

      {sport !== '0' && checkSportTypes(sport) && sports[sport] && sports[sport].length > 0 && (
        <>
          <Row>
            <Col></Col>
            <Col>
              <Row>
                <Col xs={2} className='textTitle'></Col>
                <Col xs={5} className='textTitle' style={{textAlign: 'end' }}>
                  1
                </Col>
                <Col xs={5} className='textTitle'>
                  2
                </Col>
              </Row>
            </Col>
          </Row>
        </>
      )}

      {sport !== '0' && !checkSportTypes(sport) && sports[sport] && sports[sport].length > 0 && (
        <>
          <Row>
            <Col></Col>
            <Col>
              <Row>
                <Col xs={4} className='textTitle' style={{textAlign: 'end' }}>
                  1
                </Col>
                <Col className='textTitle' xs={4}>
                  0
                </Col>
                <Col xs={4} className='textTitle' style={{textAlign: 'justify' }}>
                  2
                </Col>
              </Row>
            </Col>
          </Row>
        </>
      )}
      <div className="MatchCardListWrapper">
        <div className="MatchCardListWrapper" ref={matchCardListRef}>{getBets()}</div>
      </div>
    </div>
  );
}

export default SportDetail;
